/* AuditPolicyResultsViewRenderer.js */

"use strict";
const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const DistinctValuesFeed = require("../../../../lib/DistinctValuesFeed");
const _ = require("lodash");
const Registrations = require("../../../../../RegistrationNamespaces");
/**
 * @param {AuditPolicyResultsViewComponent} model
 */
class AuditPolicyResultsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("results-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._summarySection = document.createElement("div");
        this._gridSection = document.createElement("div");
        this._emptyContainer = document.createElement("div");

        this._filterCont = document.createElement("div");
        this._filterCont.classList.add("filter-container");

        this._policyResultsGrid = undefined;
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        if (this._policyResultsGrid) {
            this._policyResultsGrid.on(
                "UserSorted",
                (this._onGridUserSorted = this._onGridUserSorted.bind(this))
            );
        }

        if (this._executionEntity) {
            this._executionEntity.addListener(
                "PropertiesUpdated",
                (this._onEntityUpdated = this._onEntityUpdated.bind(this))
            );
        }
        this._model.on(
            "updateResultView",
            (this._onUpdateResults = this._onUpdateResults.bind(this))
        );
    }

    _unregisterListeners() {
        if (this._policyResultsGrid) {
            this._policyResultsGrid.removeListener("UserSorted", this._onGridUserSorted);
        }

        if (this._executionEntity) {
            this._executionEntity.removeListener("PropertiesUpdated", this._onEntityUpdated);
        }
        this._model.removeListener("updateResultView", this._onUpdateResults);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [
            this._policyResultsGrid ? this._policyResultsGrid.shutdown() : Promise.resolve(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._model = null;
                this._executionEntity = null;
                this._summarySection = null;
                this._gridSection = null;
                this._emptyContainer = null;

                this._rootElem.remove();
                this._rootElem = null;
            },
            (err) => {
                this._logger.warn({error: err}, "AuditPolicyResultsViewRenderer.shutdown() error.");
            }
        );
    }

    render() {
        this.rootElement.innerHTML = "";
        this._filterCont.innerHTML = "";
        this.rootElement.appendChild(this._summarySection);
        this.rootElement.appendChild(this._filterCont);
        this.rootElement.appendChild(this._gridSection);
        this.rootElement.appendChild(this._emptyContainer);

        const proms = [];
        proms.push(this.model.whenModelIsReady());
        if (this._policyResultsGrid) {
            proms.push(this._policyResultsGrid.shutdown());
        }

        return this.model.withLoadingVeil(Promise.all(proms)).then((data) => {
            const modelData = data[0];
            const [executionEntity, pagingFeed] = modelData || [];
            if (executionEntity) {
                this._executionEntity = executionEntity;
                this._renderSummary(executionEntity.getPristineProperties());
                if (this._model._isSubView) {
                    this._addBackButton();
                }
                if (
                    !_.isUndefined(this._executionEntity.violationCount) &&
                    this._executionEntity.violationCount > 0
                ) {
                    if (_.isEmpty(pagingFeed.cache)) {
                        this._renderEmpty("No violations to display");
                    } else {
                        this._renderGrid(pagingFeed, executionEntity);
                        // this._addViewActions();
                    }
                } else {
                    // this._addViewActions();
                    this._renderEmpty("No violations found");
                }
                this._addViewActions();
                this._registerListeners();
            } else {
                this._renderEmpty();
            }
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _addViewActions() {
        if (!this._viewActionsAdded) {
            // this._model.toggleViewAction({
            //     type: "normal",
            //     displayName: "Download Results",
            //     name: "cancel",
            //     handler: (this._onDownloadResults = this._onDownloadResults.bind(this))
            // });
            // this._model.toggleViewAction({
            //     type: "normal",
            //     displayName: "Download Details",
            //     name: "downloadDetails",
            //     handler: (this._onDownloadDetails = this._onDownloadDetails.bind(this))
            // });
            this._model.toggleViewAction({
                type: "normal",
                displayName: "Download",
                name: "downloadAll",
                handler: (this._onDownloadAll = this._onDownloadAll.bind(this)),
            });
            this._viewActionsAdded = true;
        }
    }

    _addBackButton() {
        this._model._gameEditor.addViewAction({
            type: "normal",
            displayName: "Back",
            name: "back",
            handler: this._onBackButtonClick.bind(this),
        });
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/
    _onGridUserSorted(eventData) {
        this.model.onUserSorted(eventData);
    }

    _onDownloadResults() {
        this._onDownloadWorkResults("results");
    }

    _onDownloadDetails() {
        this._onDownloadWorkResults("details");
    }

    async _onDownloadAll() {
        this.model._gameEditor.disableViewAction("downloadAll");
        this._model._modalUserFeedback.displayLoading({
            message: "Preparing download...",
            showHideButton: true,
        });
        try {
            await this._downloadAllResults();
        } catch (err) {
            if (err && !err.userRejected) {
                this._logger.warn(
                    {error: err},
                    "AuditPolicyResultsViewRenderer._onDownloadAll() - Error"
                );
            }
        } finally {
            this._model._modalUserFeedback.hideDisplayed();
            this.model._gameEditor.enableViewAction("downloadAll");
        }
    }

    async _downloadAllResults() {
        const reports = await this._model._executionEntity.generateWorkReports(),
            fileConfig = {},
            fileName = "Audit_Execution_Results.zip";
        let type = "text/plain";
        Object.values(reports).forEach(({name, reportType, contents}) => {
            if (reportType === "json") {
                type = "text/json";
                contents = JSON.stringify(contents, null, "\t");
            }
            fileConfig[`${name.replace(/[/,: |]/g, "_")}.${reportType}`] = {
                type,
                content: contents,
            };
        });

        this.model._fileFactory.saveStringsAsZip(fileConfig, fileName);
    }

    _onDownloadWorkResults(type) {
        this.model._gameEditor.disableViewAction("cancel");
        this.model._gameEditor.disableViewAction("downloadDetails");
        this.model
            .withLoadingVeil(
                this.model.downloadWorkResults(type).finally(() => {
                    this.model._gameEditor.enableViewAction("cancel");
                    this.model._gameEditor.enableViewAction("downloadDetails");
                })
            )
            .catch(console.error.bind(console));
    }

    _onEntityUpdated(entityData) {
        this._renderSummary(entityData);
    }

    _onBackButtonClick() {
        if (this._model._isFrom === "AuditSummary") {
            this._model._gameEditor.setActiveView(Registrations.DIE_VIEW_AUDIT_SUMMARY, [
                {
                    value: {
                        fqn: Registrations.AUDIT_POLICY_EDITOR_VIEW_RESULTS,
                    },
                },
            ]);
        } else {
            this._model._gameEditor.setActiveView(Registrations.AUDIT_POLICY_EDITOR_VIEW_HISTORY, [
                {
                    value: {
                        fqn: Registrations.AUDIT_POLICY_EDITOR_VIEW_RESULTS,
                    },
                },
            ]);
        }
    }

    _onUpdateResults() {
        this._unregisterListeners();
        this.render();
    }
    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    _renderSummary(executionEntityData) {
        this._summarySection.innerHTML = "";
        this._summarySection.classList.add("summary-section");

        const {
            title,
            completedOn,
            deviceCount = "",
            devicesInViolationCount = "",
            deviceErrorCount = "",
            // violationCount = "",
            devicesNotInViolationCount = "",
            // deviceSuccessCount = "",
            devicesSkippedCount = "",
            criticalViolationCount = "",
            infoViolationCount = "",
            majorViolationCount = "",
            minorViolationCount = "",
            status,
            auditPolicy: {name, description} = {},
        } = executionEntityData;

        let completedOnValue = dateToLocal(completedOn);
        completedOnValue = completedOnValue ? completedOnValue : _.upperFirst(_.lowerCase(status));
        let deviceCountValue = _.isNumber(deviceCount) ? deviceCount : 0;
        let deviceErrorCountValue = _.isNumber(deviceErrorCount) ? deviceErrorCount : 0;
        let devicesInViolationCountValue = _.isNumber(devicesInViolationCount)
            ? devicesInViolationCount
            : 0;
        let devicesNotInViolationCountValue = _.isNumber(devicesNotInViolationCount)
            ? devicesNotInViolationCount
            : 0;
        // let totalViolationCountValue = _.isNumber(violationCount) ? violationCount : "";
        let devicesSkippedCountValue = _.isNumber(devicesSkippedCount) ? devicesSkippedCount : 0;

        let criticalViolationCountValue = _.isNumber(criticalViolationCount)
            ? criticalViolationCount
            : "-";
        let infoViolationCountValue = _.isNumber(infoViolationCount) ? infoViolationCount : "-";
        let majorViolationCountValue = _.isNumber(majorViolationCount) ? majorViolationCount : "-";
        let minorViolationCountValue = _.isNumber(minorViolationCount) ? minorViolationCount : "-";

        let summaryTemplate = `
            <div class="summary">
                <div class="summary-column">
                    <div class="summary-item"><span title="Name: ${
                        title ? title : "-"
                    }" class="data-value">Name: ${title ? title : "-"}</span></div>
                    <div class="summary-item"><span title="End Time: ${completedOnValue}" class="data-value">End Time: ${completedOnValue}</span></div>
                    <div class="summary-item"><span class="data-label">Policy Name</span><span title="Policy Name: ${
                        name ? name : "-"
                    }" class="data-value">${name ? name : "-"}</span></div>
                    <div class="summary-item description-section"><span class="data-label">Description</span><div title="Description: ${
                        description ? description : "-"
                    }" class="data-value description-content">${
            description ? description : "-"
        }</div></div>
                </div>
                <!--div class="summary-column">
                    <div class="summary-item"><span class="data-label">Policy Name</span><span title="${name}" class="data-value">${name}</span></div>
                    <div class="summary-item description-section"><span class="data-label">Description</span><div title="${description}" class="data-value description-content">${description}</div></div>
                </div-->
                <div class="summary-column right summary">
                    <div class="summary-column">
                        <div class="summary-item"><span title="${deviceCountValue}" class="data-value">${deviceCountValue}</span><span class="data-label">Total Devices</span></div>
                        <div class="summary-item"><span title="${devicesNotInViolationCountValue}" class="data-value">${devicesNotInViolationCountValue}</span><span class="data-label">Passed Devices</span></div>
                        <div class="summary-item"><span title="${devicesInViolationCountValue}" class="data-value">${devicesInViolationCountValue}</span><span class="data-label">Failed Devices</span></div>
                        <div class="summary-item"><span title="${devicesSkippedCountValue}" class="data-value">${devicesSkippedCountValue}</span><span class="data-label">Skipped Devices</span></div>
                        <div class="summary-item"><span title="${deviceErrorCountValue}" class="data-value">${deviceErrorCountValue}</span><span class="data-label">Device Errors</span></div>
                    </div>
                    <div class="summary-column right violations">
                        <div class="summary-item"><span title="${criticalViolationCountValue}" class="data-value">${criticalViolationCountValue}</span><span class="data-label">Critical</span></div>
                        <div class="summary-item"><span title="${majorViolationCountValue}" class="data-value">${majorViolationCountValue}</span><span class="data-label">Major</span></div>
                        <div class="summary-item"><span title="${minorViolationCountValue}" class="data-value">${minorViolationCountValue}</span><span class="data-label">Minor</span></div>
                        <div class="summary-item"><span title="${infoViolationCountValue}" class="data-value">${infoViolationCountValue}</span><span class="data-label">Informational</span></div>
                    </div>
                </div>
            </div>
        `;

        this._summarySection.innerHTML = summaryTemplate;
    }

    _renderGrid(pagingFeed) {
        this._gridSection.classList.add("grid-section");
        this._gridSection.innerHTML = "";
        this._policyResultsGrid = new EntityGrid({
            gridKey: this.model.gridKey,
            columns: this.model.columnConfig,
            pagingFeed: pagingFeed,
            selectable: true,
            showHeader: true,
            showLinesNumber: this.model.gameEditorRootElement.querySelector(
                "game-editor-view-actions-gl left-container"
            ),
            modalUserFeedback: this._model._modalUserFeedback,
            groupingContainer:
                this._model._gameEditor.renderer._rootElem.querySelector(".filter-container"),
            groupingValuesFeed: new DistinctValuesFeed({
                startFeed: (fieldName, query) =>
                    this._model._executionEntity.startRelatedDistinctValueResultsFeed(
                        fieldName,
                        query
                    ),
            }),
            filterComponent:
                this._model._gameEditor.renderer._rootElem.querySelector(".filter-container"),
            advancedFilterComponent:
                this._model._gameEditor.renderer._rootElem.querySelector(".filter-container"),
        });

        this._policyResultsGrid.renderer.renderPromise
            .then(() => {
                // this._model._pagingFeed.setSort({snapshotName: 1});
                this._gridSection.appendChild(this._policyResultsGrid.renderer.rootElement);
                return Promise.resolve();
            })
            .then(() => {
                // if (this._policyResultsGrid.groupedBy !== "snapshotName") {
                //     return this._policyResultsGrid.setGroupBy("snapshotName");
                // }
            });

        //this._gridSection.appendChild(this._policyResultsGrid.renderer.rootElement);
    }

    _renderEmpty(msg = "No results to display") {
        this._emptyContainer.classList.add("empty-container");
        this._emptyContainer.innerHTML = msg;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = AuditPolicyResultsViewRenderer;

function dateToLocal(timeStamp) {
    if (!timeStamp) {
        return "";
    }
    const date = new Date(timeStamp);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
}
